import React, { useState } from 'react';
import { Box, Button, Typography, Switch, FormControlLabel } from '@mui/material';
import { ERPUIProvider } from '../providers/ERPUIProvider';
import ShareModalEnhanced from '../share-modal/share-modal-enhanced';
import HeaderEnhanced from '../header/header-enhanced';

/**
 * Example component demonstrating flexible Redux usage
 * This shows how components can work with or without Redux
 */
const FlexibleReduxExample: React.FC = () => {
  const [useRedux, setUseRedux] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Mock data for non-Redux usage
  const [users] = useState([
    { id: 1, first_name: 'John', last_name: 'Doe', label: 'John Doe', value: 1 },
    { id: 2, first_name: 'Jane', last_name: 'Smith', label: 'Jane Smith', value: 2 },
  ]);
  
  const [departments] = useState([
    { id: 1, name: 'Engineering', label: 'Engineering', value: 1 },
    { id: 2, name: 'Marketing', label: 'Marketing', value: 2 },
  ]);
  
  const [languages] = useState([
    { code: 'en', label: 'English', direction: 'ltr' },
    { code: 'es', label: 'Spanish', direction: 'ltr' },
    { code: 'ar', label: 'Arabic', direction: 'rtl' },
  ]);
  
  const [currentLanguage, setCurrentLanguage] = useState(languages[0]);
  const [loading, setLoading] = useState(false);

  // Mock API functions
  const fetchUsersAndDepartments = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoading(false);
  };

  const handleLanguageChange = (languageCode: string) => {
    const language = languages.find(lang => lang.code === languageCode);
    if (language) {
      setCurrentLanguage(language);
    }
  };

  const handleShareSubmit = (data: any) => {
    setShowShareModal(false);
  };

  return (
    <ERPUIProvider useRedux={useRedux}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Flexible Redux Integration Example
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          This example demonstrates how ERP UI Library components can work with or without Redux.
          Toggle the switch below to see the difference.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={useRedux}
              onChange={(e) => setUseRedux(e.target.checked)}
            />
          }
          label={`Use Redux: ${useRedux ? 'Enabled' : 'Disabled'}`}
          sx={{ mb: 3 }}
        />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current Mode: {useRedux ? 'Redux-based' : 'Prop-based'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {useRedux 
              ? 'Components are using Redux for state management'
              : 'Components are using props and local state for data'
            }
          </Typography>
        </Box>

        {/* Header Example */}
        <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Header Component
          </Typography>
          <HeaderEnhanced
            useRedux={useRedux}
            languages={useRedux ? undefined : languages}
            currentLanguage={useRedux ? undefined : currentLanguage}
            languagesLoading={useRedux ? undefined : loading}
            onLanguageChange={useRedux ? undefined : handleLanguageChange}
            onFetchLanguages={useRedux ? undefined : fetchUsersAndDepartments}
          />
        </Box>

        {/* Share Modal Example */}
        <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Share Modal Component
          </Typography>
          <Button
            variant="contained"
            onClick={() => setShowShareModal(true)}
            sx={{ mb: 2 }}
          >
            Open Share Modal
          </Button>
          
          <ShareModalEnhanced
            open={showShareModal}
            onClose={() => setShowShareModal(false)}
            title="Share Document"
            toggleSwitch={true}
            onSubmit={handleShareSubmit}
            useRedux={useRedux}
            users={useRedux ? undefined : users}
            departments={useRedux ? undefined : departments}
            loading={useRedux ? undefined : loading}
            onFetchUsersAndDepartments={useRedux ? undefined : fetchUsersAndDepartments}
          />
        </Box>

        {/* Code Example */}
        <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Code Example
          </Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.875rem',
            }}
          >
            {useRedux ? `
// Redux-based usage
<ERPUIProvider useRedux={true} reduxStore={store} apiClient={apiClient}>
  <HeaderEnhanced useRedux={true} />
  <ShareModalEnhanced useRedux={true} />
</ERPUIProvider>
            ` : `
// Prop-based usage
<ERPUIProvider useRedux={false}>
  <HeaderEnhanced
    useRedux={false}
    languages={languages}
    currentLanguage={currentLanguage}
    onLanguageChange={handleLanguageChange}
  />
  <ShareModalEnhanced
    useRedux={false}
    users={users}
    departments={departments}
    loading={loading}
    onFetchUsersAndDepartments={fetchUsersAndDepartments}
  />
</ERPUIProvider>
            `}
          </Box>
        </Box>
      </Box>
    </ERPUIProvider>
  );
};

export default FlexibleReduxExample;